import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { macroDetailData } from '../../../data/macroData';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface FiscalPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const FiscalPanel: React.FC<FiscalPanelProps> = ({ isMinimized, onMinimize }) => {
  const { fiscal } = macroDetailData;
  const budgetImprovement = fiscal.budgetBalance - fiscal.budgetBalancePrev;
  const debtImprovement = fiscal.publicDebt - fiscal.publicDebtPrev;
  const maxSpending = Math.max(...fiscal.breakdown.map(b => b.pctGDP));

  return (
    <Panel title="FISCAL & DEBT" subtitle="% GDP" status="static" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col gap-2 h-full">
        {/* Budget Balance & Public Debt */}
        <div className="grid grid-cols-2 gap-px bg-terminal-border rounded overflow-hidden">
          <div className="bg-terminal-bg p-2 text-center">
            <div className="text-xs text-gray-500 uppercase">Budget Balance</div>
            <div className="text-xl font-bold text-data-red">{fiscal.budgetBalance}%</div>
            <div className="flex items-center justify-center gap-1 text-data-green text-xs">
              <TrendingUp size={12} />
              <span>+{budgetImprovement.toFixed(1)}%</span>
            </div>
          </div>
          <div className="bg-terminal-bg p-2 text-center">
            <div className="text-xs text-gray-500 uppercase">Public Debt</div>
            <div className="text-xl font-bold text-data-amber">{fiscal.publicDebt}%</div>
            <div className="flex items-center justify-center gap-1 text-data-green text-xs">
              <TrendingDown size={12} />
              <span>{debtImprovement}%</span>
            </div>
          </div>
        </div>

        {/* Revenue vs Expenditure */}
        <div className="bg-terminal-bg rounded p-2">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 w-20">Revenue</span>
              <div className="flex-1 h-2.5 bg-terminal-border rounded overflow-hidden">
                <div
                  className="h-full bg-data-green/60 rounded"
                  style={{ width: `${(fiscal.revenue / 35) * 100}%` }}
                />
              </div>
              <span className="text-xs text-data-green w-12 text-right">{fiscal.revenue}%</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 w-20">Expenditure</span>
              <div className="flex-1 h-2.5 bg-terminal-border rounded overflow-hidden">
                <div
                  className="h-full bg-data-red/60 rounded"
                  style={{ width: `${(fiscal.expenditure / 35) * 100}%` }}
                />
              </div>
              <span className="text-xs text-data-red w-12 text-right">{fiscal.expenditure}%</span>
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-1.5 pt-1.5 border-t border-terminal-border">
            External Debt: <span className="text-white">${fiscal.externalDebt}B</span>
          </div>
        </div>

        {/* Spending Breakdown */}
        <div className="flex-1 bg-terminal-bg rounded p-2 min-h-0">
          <div className="text-xs text-gray-500 uppercase mb-1.5">Spending Breakdown</div>
          <div className="space-y-1.5">
            {fiscal.breakdown.map((item) => (
              <div key={item.item} className="flex items-center gap-2">
                <span className="text-xs text-gray-400 w-20 truncate">{item.item}</span>
                <div className="flex-1 h-2 bg-terminal-border rounded overflow-hidden">
                  <div
                    className="h-full bg-data-amber/50 rounded"
                    style={{ width: `${(item.pctGDP / maxSpending) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-gray-300 w-10 text-right">{item.pctGDP}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.IMF} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default FiscalPanel;
